import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Plus,
  Pencil,
  Eye,
  Search,
  Trash2,
} from 'lucide-react';
import { useApp } from '@/store';
import { useT } from '@/lib/i18n';
import { useConfirm } from '@/components/ConfirmDialog';
import { fmtDate } from '@/lib/utils';
import type { I18nKey } from '@/lib/i18n';
import type { Customer, Extinguisher, Hose, Visit } from '@/data/types';
import { VisitStatus } from '@/data/types';
import { assignWorker, visibleVisits, visitWorker } from '@/lib/visits';
import { deriveNextInspection } from '@/lib/customer-dates';
import { getDecoded } from '@/lib/auth';
import { SectionId } from '@/lib/section-permissions';
import { useSectionRights } from '@/lib/use-section-rights';
import { CompleteInspectionDialog } from './CompleteInspectionDialog';
import { PageHeader, ResponsiveGrid } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const WEEKDAYS_KEY: I18nKey[] = [
  'calendar.today', // placeholder — we use inline short names
];

const STATUS_VARIANT: Record<
  VisitStatus,
  'info' | 'warning' | 'success' | 'secondary'
> = {
  [VisitStatus.PLANNED]: 'info',
  [VisitStatus.CONFIRMED]: 'warning',
  [VisitStatus.COMPLETED]: 'success',
  [VisitStatus.CANCELLED]: 'secondary',
};

const STATUS_I18N: Record<VisitStatus, I18nKey> = {
  [VisitStatus.PLANNED]: 'calendar.status.planned',
  [VisitStatus.CONFIRMED]: 'calendar.status.confirmed',
  [VisitStatus.COMPLETED]: 'calendar.status.completed',
  [VisitStatus.CANCELLED]: 'calendar.status.cancelled',
};

const STATUS_DOT: Record<VisitStatus, string> = {
  [VisitStatus.PLANNED]: 'bg-blue-500',
  [VisitStatus.CONFIRMED]: 'bg-amber-500',
  [VisitStatus.COMPLETED]: 'bg-emerald-500',
  [VisitStatus.CANCELLED]: 'bg-muted-foreground',
};

// i18n-ignore: "Mon"
// i18n-ignore: "Tue"
// i18n-ignore: "Wed"
// i18n-ignore: "Thu"
// i18n-ignore: "Fri"
// i18n-ignore: "Sat"
// i18n-ignore: "Sun"
const WEEKDAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year: number, month: number) {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1; // Monday = 0
}

function dateKey(y: number, m: number, d: number) {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

const emptyVisit = (date: string): Visit => ({
  id: `V-${Date.now()}`,
  customerId: '',
  date,
  status: VisitStatus.PLANNED,
});

// Client #27: the picker used to mount one <SelectItem> per customer. With the
// anchor client's 5000+ records that froze the dialog for seconds on every
// open, so only the first PICKER_LIMIT matches of the search are mounted and
// the rest stay behind the query.
const PICKER_LIMIT = 50;

// Matches the customer-dates.ts fallback: the Swiss extinguisher maintenance
// cycle. settings.inspectionInterval seeds to 0 before the tenant config has
// synced, and a 0-year interval would mark every device on site as due.
const DEFAULT_INTERVAL_YEARS = 3;

// Client #70: before driving out, the technician wants to know what is actually
// waiting at the site — not just that "a visit" is booked.
interface VisitSummary {
  extinguishersDue: number;
  /** Distinct model names of the due extinguishers (so the van is loaded right). */
  extinguisherModels: string[];
  hosesDue: number;
  refillsDue: number;
}

// A device counts as due when its effective next-service date falls on or
// before the day of the visit. The effective date is *not* the stored one —
// customer-dates.ts owns the rule that repairs the migrated Excel dates, so we
// reuse it one device at a time instead of re-deriving it here. That keeps this
// panel from drifting away from Reminders / Overview.
// ISO yyyy-mm-dd strings compare lexicographically.
function isExtinguisherDue(
  e: Extinguisher,
  intervalYears: number,
  onDate: string
): boolean {
  const due = deriveNextInspection(
    { extinguishers: [e], hoses: [] },
    intervalYears
  );
  return !!due && due <= onDate;
}

function isHoseDue(h: Hose, intervalYears: number, onDate: string): boolean {
  const due = deriveNextInspection(
    { extinguishers: [], hoses: [h] },
    intervalYears
  );
  return !!due && due <= onDate;
}

// Refill rule (client #70), deliberately kept simple and legible: an
// extinguisher needs a filling when it has never been refilled, or when its
// last refill is older than one inspection interval on the day of the visit.
// Anything unparseable counts as due — an unknown filling date is exactly the
// case the technician must check on site.
function isRefillDue(
  e: Extinguisher,
  intervalYears: number,
  onDate: string
): boolean {
  const [y, m, d] = (e.refilled ?? '').split('-');
  if (!y || !m || !d) return true;
  return `${Number(y) + intervalYears}-${m}-${d}` <= onDate;
}

function summariseVisit(
  c: Customer | undefined,
  intervalYears: number,
  onDate: string
): VisitSummary {
  const extinguishers = c?.extinguishers ?? [];
  const dueExtinguishers = extinguishers.filter(e =>
    isExtinguisherDue(e, intervalYears, onDate)
  );
  return {
    extinguishersDue: dueExtinguishers.length,
    extinguisherModels: [
      ...new Set(
        dueExtinguishers
          .map(e => e.model?.trim())
          .filter((m): m is string => !!m)
      ),
    ],
    hosesDue: (c?.hoses ?? []).filter(h => isHoseDue(h, intervalYears, onDate))
      .length,
    refillsDue: extinguishers.filter(e => isRefillDue(e, intervalYears, onDate))
      .length,
  };
}

void WEEKDAYS_KEY; // suppress unused

export function CalendarPage() {
  const t = useT();
  const customers = useApp(s => s.customers);
  const confirm = useConfirm();
  const visits = useApp(s => s.visits);
  const workers = useApp(s => s.workers);
  const upsertVisit = useApp(s => s.upsertVisit);
  const deleteVisit = useApp(s => s.deleteVisit);
  const toast = useApp(s => s.toast);
  // Section rights (#95): visits are the calendar's; starting the maintenance
  // from a visit is the maintenance right.
  const rights = useSectionRights();
  const canCal = rights[SectionId.CALENDAR];
  const canStartMaintenance = rights[SectionId.MAINTENANCE].create;
  const canOpenCustomer = rights[SectionId.CUSTOMERS].read;
  const intervalSetting = useApp(s => s.settings.inspectionInterval);
  const intervalYears =
    intervalSetting > 0 ? intervalSetting : DEFAULT_INTERVAL_YEARS;

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [filterWorker, setFilterWorker] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editVisit, setEditVisit] = useState<Visit | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  // Query for the searchable customer picker (client #27).
  const [customerQuery, setCustomerQuery] = useState('');
  // Day-panel boxes are collapsed by default (client #70) — same
  // expandedGroups/toggleGroup pattern as the Overview billing groups.
  const [expandedVisits, setExpandedVisits] = useState<Set<string>>(new Set());

  const toggleVisit = (id: string) =>
    setExpandedVisits(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  // Store-held so an in-progress maintenance survives leaving this page —
  // adding a device from the dialog routes to the device form (client #64).
  const activeVisitId = useApp(s => s.activeVisitId);
  const setActiveVisit = useApp(s => s.setActiveVisit);
  const completing = visits.find(v => v.id === activeVisitId) ?? null;

  // Whether the open visit dialog may change anything: a new visit needs
  // Erstellen, an existing one Bearbeiten. Without it the dialog is read-only.
  const editingExisting =
    !!editVisit && visits.some(v => v.id === editVisit.id);
  const dialogReadOnly = editingExisting ? !canCal.edit : !canCal.create;

  const handleComplete = () => {
    if (!editVisit || !editVisit.customerId) return;
    // persist assignment/edits before completing — when the user may edit
    if (!dialogReadOnly) upsertVisit(editVisit);
    setDialogOpen(false);
    setActiveVisit(editVisit.id);
  };

  // A technician sees only their assigned visits (UI scope); admin/staff see all.
  const scopedVisits = useMemo(() => {
    const d = getDecoded();
    const email = d?.email ?? d?.sub ?? '';
    const user = d
      ? {
          roles: d.roles ?? [],
          email,
          workerName: workers.find(
            w => w.email?.toLowerCase() === email.toLowerCase()
          )?.name,
        }
      : null;
    return visibleVisits(visits, user);
  }, [visits, workers]);

  const filteredVisits = useMemo(
    () =>
      filterWorker && filterWorker !== 'all'
        ? scopedVisits.filter(v => v.worker === filterWorker)
        : scopedVisits,
    [scopedVisits, filterWorker]
  );

  const visitsByDate = useMemo(() => {
    const map: Record<string, Visit[]> = {};
    filteredVisits.forEach(v => {
      if (!map[v.date]) map[v.date] = [];
      map[v.date].push(v);
    });
    // Chronological within the day, with the untimed appointments first — a
    // job with no fixed time is one the technician slots in around the ones
    // that do (client #93). HH:mm sorts correctly as a string.
    Object.values(map).forEach(day =>
      day.sort((a, b) => (a.time ?? '').localeCompare(b.time ?? ''))
    );
    return map;
  }, [filteredVisits]);

  const prev = useCallback(() => {
    if (month === 0) {
      setMonth(11);
      setYear(y => y - 1);
    } else setMonth(m => m - 1);
  }, [month]);

  const next = useCallback(() => {
    if (month === 11) {
      setMonth(0);
      setYear(y => y + 1);
    } else setMonth(m => m + 1);
  }, [month]);

  const goToday = useCallback(() => {
    setYear(today.getFullYear());
    setMonth(today.getMonth());
  }, [today]);

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfWeek(year, month);

  // i18n-ignore: "January"
  // i18n-ignore: "February"
  // i18n-ignore: "March"
  // i18n-ignore: "April"
  // i18n-ignore: "May"
  // i18n-ignore: "June"
  // i18n-ignore: "July"
  // i18n-ignore: "August"
  // i18n-ignore: "September"
  // i18n-ignore: "October"
  // i18n-ignore: "November"
  // i18n-ignore: "December"
  const monthLabel = new Date(year, month).toLocaleString('default', {
    month: 'long',
    year: 'numeric',
  });

  const customerName = (id: string) =>
    customers.find(c => c.id === id)?.name ?? id;

  const openAdd = (date: string) => {
    setEditVisit(emptyVisit(date));
    setCustomerQuery('');
    setDialogOpen(true);
  };

  const openEdit = (v: Visit) => {
    setEditVisit({ ...v });
    setCustomerQuery('');
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (!editVisit || !editVisit.customerId) return;
    upsertVisit(editVisit);
    toast(t('calendar.visitSaved'));
    setDialogOpen(false);
    setEditVisit(null);
  };

  const handleDelete = async (v: Visit) => {
    // A visit carries the readings taken on site; deleting one without asking
    // loses that work irrecoverably (client #85).
    const who =
      customers.find(c => c.id === v.customerId)?.name ?? v.customerId;
    if (!(await confirm(`${t('common.delete')} ${who} — ${fmtDate(v.date)}?`)))
      return;
    deleteVisit(v.id);
    toast(t('calendar.visitDeleted'));
  };

  const todayKey = dateKey(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  const activeWorkers = workers.filter(w => w.active);

  const dayVisits = useMemo(
    () => (selectedDate ? (visitsByDate[selectedDate] ?? []) : []),
    [selectedDate, visitsByDate]
  );

  // Client #70: summarise the site's equipment once per open day panel rather
  // than inside the render of every collapsible box.
  const visitSummaries = useMemo(() => {
    const map: Record<string, VisitSummary> = {};
    for (const v of dayVisits) {
      map[v.id] = summariseVisit(
        customers.find(c => c.id === v.customerId),
        intervalYears,
        v.date
      );
    }
    return map;
  }, [dayVisits, customers, intervalYears]);

  // Client #27: the whole customer array still lives in the store, but only the
  // capped list of query matches is ever turned into JSX. The current selection
  // is force-included because Radix renders the trigger label from the mounted
  // <SelectItem> — filtering it away would blank out the field.
  const selectedCustomerId = editVisit?.customerId ?? '';
  const picker = useMemo(() => {
    const q = customerQuery.trim().toLowerCase();
    const matches = q
      ? customers.filter(c =>
          `${c.name} ${c.id} ${c.objectAddress?.city ?? ''}`
            .toLowerCase()
            .includes(q)
        )
      : customers;
    const hidden = Math.max(0, matches.length - PICKER_LIMIT);
    const shown = matches.slice(0, PICKER_LIMIT);
    if (selectedCustomerId && !shown.some(c => c.id === selectedCustomerId)) {
      const selected = customers.find(c => c.id === selectedCustomerId);
      if (selected) shown.unshift(selected);
    }
    return { shown, hidden };
  }, [customers, customerQuery, selectedCustomerId]);

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <PageHeader
        title={t('calendar.title')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Select value={filterWorker} onValueChange={setFilterWorker}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t('calendar.allWorkers')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('calendar.allWorkers')}</SelectItem>
                {activeWorkers.map(w => (
                  <SelectItem key={w.id} value={w.name}>
                    {w.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      />

      {/* Month nav */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={prev}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={next}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={goToday}>
            {t('calendar.today')}
          </Button>
        </div>
        {/* i18n-ignore: "monthLabel" */}
        <h2 className="text-lg font-semibold">{monthLabel}</h2>
      </div>

      {/* Calendar grid */}
      <Card>
        <CardContent className="p-0 sm:p-2">
          {/* Weekday headers */}
          <div className="grid grid-cols-7 sm:grid-cols-7 border-b">
            {WEEKDAY_SHORT.map(d => (
              <div
                key={d}
                className="py-2 text-center text-xs font-medium text-muted-foreground"
              >
                {d}
              </div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7 sm:grid-cols-7">
            {/* Empty cells before first day */}
            {Array.from({ length: firstDay }).map((_, i) => (
              <div
                key={`empty-${i}`}
                className="min-h-[72px] sm:min-h-[96px] border-b border-r p-1 bg-muted/20"
              />
            ))}

            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dk = dateKey(year, month, day);
              const isToday = dk === todayKey;
              const isSelected = dk === selectedDate;
              const dv = visitsByDate[dk] ?? [];

              return (
                <motion.div
                  key={dk}
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.1 }}
                  onClick={() => setSelectedDate(isSelected ? null : dk)}
                  className={`min-h-[72px] sm:min-h-[96px] border-b border-r p-1 cursor-pointer transition-colors ${
                    isSelected
                      ? 'bg-primary/10 ring-1 ring-primary'
                      : 'hover:bg-accent/40'
                  }`}
                >
                  <div className="flex items-center justify-between px-1">
                    <span
                      className={`text-xs font-medium ${
                        isToday
                          ? 'flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground'
                          : 'text-foreground'
                      }`}
                    >
                      {day}
                    </span>
                    {dv.length > 0 && (
                      <span className="text-[10px] text-muted-foreground">
                        {dv.length}
                      </span>
                    )}
                  </div>

                  {/* Visit dots */}
                  <div className="mt-1 space-y-0.5">
                    {dv.slice(0, 3).map(v => (
                      <div
                        key={v.id}
                        className="flex items-center gap-1 rounded px-1 py-0.5 text-[10px] leading-tight truncate bg-accent/50"
                      >
                        <span
                          className={`h-1.5 w-1.5 shrink-0 rounded-full ${STATUS_DOT[v.status]}`}
                        />
                        {v.time && (
                          <span className="shrink-0 tabular-nums text-muted-foreground">
                            {v.time}
                          </span>
                        )}
                        <span className="truncate hidden sm:inline">
                          {customerName(v.customerId)}
                        </span>
                      </div>
                    ))}
                    {dv.length > 3 && (
                      <span className="text-[10px] text-muted-foreground px-1">
                        +{dv.length - 3}
                      </span>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Selected day panel */}
      <AnimatePresence>
        {selectedDate && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
          >
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">{selectedDate}</h3>
                  {canCal.create && (
                    <Button size="sm" onClick={() => openAdd(selectedDate)}>
                      <Plus className="mr-1 h-4 w-4" />
                      {t('calendar.addVisit')}
                    </Button>
                  )}
                </div>

                {dayVisits.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t('calendar.noVisits')}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {/* Client #70: one collapsible box per visit — collapsed by
                        default so a busy day still fits on a phone screen, and
                        the device summary is only computed/shown on demand. */}
                    {dayVisits.map(v => {
                      const open = expandedVisits.has(v.id);
                      const summary = visitSummaries[v.id];
                      const nothingDue =
                        !summary ||
                        summary.extinguishersDue +
                          summary.hosesDue +
                          summary.refillsDue ===
                          0;
                      return (
                        <div key={v.id} className="rounded-lg border">
                          <div className="flex flex-wrap items-center gap-2 p-3">
                            {/* Dedicated toggle rather than a clickable row:
                                the row already holds a link and two icon
                                buttons, which may not nest inside a button. */}
                            <button
                              type="button"
                              onClick={() => toggleVisit(v.id)}
                              aria-expanded={open}
                              aria-label={t('calendar.visitDetails')}
                              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted/50"
                            >
                              {open ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </button>
                            <Badge variant={STATUS_VARIANT[v.status]}>
                              {t(STATUS_I18N[v.status])}
                            </Badge>
                            {v.time && (
                              <span className="font-mono text-sm tabular-nums text-muted-foreground">
                                {v.time}
                              </span>
                            )}
                            {canOpenCustomer ? (
                              <Link
                                to={`/customer/${v.customerId}`}
                                className="font-medium text-sm text-primary hover:underline"
                              >
                                {customerName(v.customerId)}
                              </Link>
                            ) : (
                              <span className="font-medium text-sm">
                                {customerName(v.customerId)}
                              </span>
                            )}
                            {v.worker && (
                              <span className="text-sm text-muted-foreground">
                                — {v.worker}
                              </span>
                            )}
                            {v.notes && (
                              <span className="text-sm text-muted-foreground italic basis-full">
                                {v.notes}
                              </span>
                            )}
                            <div className="ml-auto flex gap-1">
                              {/* Without Bearbeiten the dialog opens
                                  read-only — still the way to see the visit
                                  and to start its maintenance. */}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openEdit(v)}
                                title={
                                  canCal.edit
                                    ? t('common.edit')
                                    : t('common.view')
                                }
                                aria-label={
                                  canCal.edit
                                    ? t('common.edit')
                                    : t('common.view')
                                }
                              >
                                {canCal.edit ? (
                                  <Pencil className="h-3.5 w-3.5" />
                                ) : (
                                  <Eye className="h-3.5 w-3.5" />
                                )}
                              </Button>
                              {canCal.delete && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive"
                                  onClick={() => handleDelete(v)}
                                  title={t('common.delete')}
                                  aria-label={t('common.delete')}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          </div>

                          {open && (
                            <div className="border-t p-3">
                              {nothingDue ? (
                                <p className="text-sm text-muted-foreground">
                                  {t('calendar.nothingDue')}
                                </p>
                              ) : (
                                <>
                                  <ResponsiveGrid cols={3} gap={3}>
                                    <div>
                                      <p className="text-xs text-muted-foreground">
                                        {t('calendar.extinguishersDue')}
                                      </p>
                                      <p className="text-lg font-semibold">
                                        {summary.extinguishersDue}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-muted-foreground">
                                        {t('calendar.hosesDue')}
                                      </p>
                                      <p className="text-lg font-semibold">
                                        {summary.hosesDue}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-muted-foreground">
                                        {t('calendar.refillsDue')}
                                      </p>
                                      <p className="text-lg font-semibold">
                                        {summary.refillsDue}
                                      </p>
                                    </div>
                                  </ResponsiveGrid>
                                  {summary.extinguisherModels.length > 0 && (
                                    <p className="mt-3 text-xs text-muted-foreground">
                                      {t('calendar.dueModels')}:{' '}
                                      {summary.extinguisherModels.join(', ')}
                                    </p>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editVisit && visits.some(v => v.id === editVisit.id)
                ? t('calendar.editVisit')
                : t('calendar.addVisit')}
            </DialogTitle>
          </DialogHeader>

          {editVisit && (
            <div className="grid grid-cols-1 gap-4 py-2">
              <div className="space-y-1.5">
                <Label>{t('calendar.customer')}</Label>
                <Select
                  disabled={dialogReadOnly}
                  value={editVisit.customerId}
                  onValueChange={v =>
                    setEditVisit({ ...editVisit, customerId: v })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('calendar.selectCustomer')} />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Client #27: search box inside the dropdown. Radix runs a
                        typeahead on the content's keydown handler that would
                        pull every printable keystroke back to the option list,
                        so those are swallowed here — Escape and the arrow keys
                        still reach the dropdown, keeping keyboard navigation
                        (type to filter, arrow down into the list) intact. */}
                    <div className="sticky top-0 z-10 bg-popover p-1">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          className="h-9 pl-9"
                          value={customerQuery}
                          placeholder={t('calendar.searchCustomer')}
                          aria-label={t('calendar.searchCustomer')}
                          onChange={e => setCustomerQuery(e.target.value)}
                          onKeyDown={e => {
                            if (e.key.length === 1) e.stopPropagation();
                          }}
                        />
                      </div>
                    </div>
                    {picker.shown.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                    {picker.shown.length === 0 && (
                      <p className="px-2 py-3 text-center text-sm text-muted-foreground">
                        {t('calendar.noCustomerMatch')}
                      </p>
                    )}
                    {picker.hidden > 0 && (
                      <p className="px-2 py-1.5 text-xs text-muted-foreground">
                        +{picker.hidden} {t('calendar.moreMatches')}
                      </p>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>{t('calendar.date')}</Label>
                <Input
                  type="date"
                  disabled={dialogReadOnly}
                  value={editVisit.date}
                  onChange={e =>
                    setEditVisit({ ...editVisit, date: e.target.value })
                  }
                />
              </div>

              <div className="space-y-1.5">
                {/* Optional — most visits are booked for a day, not an hour.
                    Clearing the field puts the appointment back at the head of
                    that day's list (client #93). */}
                <Label>
                  {t('calendar.time')}{' '}
                  <span className="text-muted-foreground">
                    {t('common.optional')}
                  </span>
                </Label>
                <Input
                  type="time"
                  disabled={dialogReadOnly}
                  value={editVisit.time ?? ''}
                  onChange={e =>
                    setEditVisit({
                      ...editVisit,
                      time: e.target.value || undefined,
                    })
                  }
                />
              </div>

              {/* One technician field (client #92): it sets the Mitarbeiter
                  name and the login together, and decides which technician's
                  device receives this customer. */}
              <div className="space-y-1.5">
                <Label>{t('calendar.worker')}</Label>
                <Select
                  disabled={dialogReadOnly}
                  value={visitWorker(editVisit, workers)?.id ?? ''}
                  onValueChange={id =>
                    setEditVisit(
                      assignWorker(
                        editVisit,
                        workers.find(w => w.id === id)
                      )
                    )
                  }
                >
                  <SelectTrigger data-testid="visit-worker">
                    <SelectValue
                      placeholder={editVisit.worker || t('calendar.worker')}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {activeWorkers.map(w => (
                      <SelectItem key={w.id} value={w.id}>
                        {w.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editVisit.worker && !editVisit.assignedTechId && (
                  <p className="text-xs text-muted-foreground">
                    {t('calendar.workerNoLogin')}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label>{t('calendar.status')}</Label>
                <Select
                  disabled={dialogReadOnly}
                  value={editVisit.status}
                  onValueChange={v =>
                    setEditVisit({
                      ...editVisit,
                      status: v as VisitStatus,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(VisitStatus).map(s => (
                      <SelectItem key={s} value={s}>
                        {t(STATUS_I18N[s])}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>{t('calendar.notes')}</Label>
                <Textarea
                  disabled={dialogReadOnly}
                  value={editVisit.notes ?? ''}
                  onChange={e =>
                    setEditVisit({ ...editVisit, notes: e.target.value })
                  }
                  rows={2}
                />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
                {editVisit.status === VisitStatus.COMPLETED ? (
                  <span className="text-xs text-emerald-600">
                    {t('inspection.completed')}
                  </span>
                ) : canStartMaintenance ? (
                  // Starting the maintenance is the Wartung right (#95).
                  <Button
                    variant="secondary"
                    onClick={handleComplete}
                    disabled={!editVisit.customerId}
                  >
                    {t('inspection.complete')}
                  </Button>
                ) : (
                  <span />
                )}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                  >
                    {t('calendar.cancel')}
                  </Button>
                  {!dialogReadOnly && (
                    <Button
                      onClick={handleSave}
                      disabled={!editVisit.customerId}
                    >
                      {t('calendar.save')}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CompleteInspectionDialog
        visit={canStartMaintenance ? completing : null}
        open={!!completing && canStartMaintenance}
        onOpenChange={o => {
          if (!o) setActiveVisit(null);
        }}
      />
    </div>
  );
}
